import React from 'react';
import { gql } from '@apollo/client';
import {
  Show,
  SimpleShowLayout,
  TextField,
  EditButton,
  TopToolbar,
  useRecordContext,
  Labeled,
  Link,
} from 'react-admin';
import useCheckAccess from 'hooks/useCheckAccess';
import { ConditionSatisfactionHelper, SatisfactionOption } from '@medely/credentials-tools';
import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import { CSHCondition } from '@medely/credentials-tools/build/types/types';

const ShowActions = () => {
  const checkAccess = useCheckAccess();
  const canUpdate = checkAccess('update', 'Condition');
  if (!canUpdate) {
    return null;
  }

  return (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  );
};

const HumanizedConditionOption = ({
  option,
}: {
  option: SatisfactionOption<CSHCondition, any>;
}) => (
  <Stack spacing={2}>
    {option.qualifications.map((optionQualification, index) => (
      <>
        {index > 0 && <Divider />}
        <Stack direction="row">
          <Link to={`/Qualification/${optionQualification.qualification.id}/show`}>
            {optionQualification.qualification.long_name}
          </Link>
          {optionQualification.description && (
            <Typography>{`: ${optionQualification.description}`}</Typography>
          )}
        </Stack>
      </>
    ))}
  </Stack>
);

const HumanizedConditionOptions = ({
  options,
  userType,
}: {
  options: SatisfactionOption<CSHCondition, any>[];
  userType: string;
}) => (
  <Labeled label={`${userType} Satisfaction Options`}>
    <Box mt={2} mb={2}>
      <Stack spacing={2}>
        {options.map((option, index) => (
          <Card>
            <Box p={2}>
              <Labeled label={options.length > 1 && `Option ${index + 1}`}>
                <HumanizedConditionOption option={option} />
              </Labeled>
            </Box>
          </Card>
        ))}
      </Stack>
    </Box>
  </Labeled>
);

const HumanizedConditionField = () => {
  const condition = useRecordContext<CSHCondition>();

  if (!condition || !condition.qualifications?.[0]?.slug) {
    return null;
  }

  const conditionHelper = new ConditionSatisfactionHelper({
    condition,
    professionalQualifications: [],
    satisfiedConditionIds: [],
  });

  const professionalSatisfactionOptions = conditionHelper.availableSatisfactionOptions;
  const agencySatisfactionOptions = conditionHelper.availableAgencySatisfactionOptions;

  const distinctOptions =
    JSON.stringify(professionalSatisfactionOptions) !== JSON.stringify(agencySatisfactionOptions);

  return (
    <Box display="inline-flex" flexDirection="column">
      <HumanizedConditionOptions
        options={professionalSatisfactionOptions}
        userType={distinctOptions ? 'Professional' : ''}
      />
      {distinctOptions && (
        <HumanizedConditionOptions options={agencySatisfactionOptions} userType="Agency" />
      )}
    </Box>
  );
};

const ConditionShow = () => (
  <Show actions={<ShowActions />}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField source="label" />
      <TextField source="slug" />
      <TextField source="condition_type" />
      <TextField source="payload" />
      <HumanizedConditionField />
      <TextField source="display_category" />
    </SimpleShowLayout>
  </Show>
);

ConditionShow.query = gql`
  query GET_ONE($id: Int!) {
    condition(id: $id) {
      id
      label
      name
      slug
      condition_type
      payload
      display_category
      qualifications {
        admin_view_only
        id
        input_source
        long_name
        qualification_type
        status
        slug
        qualification_attributes {
          field_type
          id
          input_source
          name
          slug
        }
      }
    }
  }
`;

export default ConditionShow;
